


































































































































import { Component, Vue, Prop } from 'vue-property-decorator'
import { ExStore } from 'vuex'
import dayjs from 'dayjs'
import { Message } from '@/types/message'
import { CallLog } from '@/types/call'
import { dayOfWeek } from '@/constants/datetime'
import { isCordovaEnvironment } from '@/utils/cordova'
import { isRelative } from '@/types/user'

enum HistoryTypes {
  MESSAGE,
  CALL
}

type NoticeAddtionalParams = {
  timestamp: number
  type: HistoryTypes
}

type Notice =
  | (Message & NoticeAddtionalParams)
  | (CallLog & NoticeAddtionalParams)

@Component({
  components: {
    Login: () => import('@/views/Login.vue'),
    QrReader: () => import('@/components/patient/QrReader.vue'),
    VitalDisplay: () => import('@/components/patient/home/VitalDisplay.vue')
  }
})
export default class Home extends Vue {
  @Prop({ default: false }) onLoadShowQr!: boolean
  $store!: ExStore
  showQR = false

  readonly HISTORY_TYPE_MESSAGE = HistoryTypes.MESSAGE
  readonly HISTORY_TYPE_CALL = HistoryTypes.CALL

  get inCallWebView() {
    return this.$store.getters['videoCallRoom/inCallWebView']
  }

  get user() {
    return this.$store.getters['user/userInfo']
  }

  get relativeInfo() {
    return this.$store.getters['user/relativeInfo']
  }

  get relativePatient() {
    return this.relativeInfo ? this.relativeInfo.patient : null
  }

  get isRelative() {
    return this.user.isPatientRelative
  }

  get routePrefix() {
    return this.isRelative ? 'Relative' : 'Patient'
  }

  get patientId(): number | null | undefined {
    return this.user.isPatientRelative
      ? this.relativeInfo?.patient.id
      : this.user.id
  }

  get workers() {
    if (this.user.isPatientRelative) return []
    return this.$store.getters['team/workersForPatientUser']
  }

  get relatives() {
    if (this.user.isPatientRelative) return []
    return this.$store.getters['relative/activeRelativeList']
  }

  get onlineStatus() {
    return this.$store.getters['onlineStatus/onlineStatus']
  }

  get unreadMessages() {
    return this.$store.getters['message/unreadMessages']
  }

  get callLog() {
    return this.$store.getters['call/missedCallLog']
  }

  get notices() {
    const messages = this.unreadMessages.map(m => ({
      ...m,
      type: HistoryTypes.MESSAGE,
      timestamp: dayjs(m.registeredDatetime).unix()
    })) as Notice[]
    const callLog = this.callLog.map(c => ({
      ...c,
      type: HistoryTypes.CALL,
      timestamp: dayjs(c.registeredDatetime).unix()
    })) as Notice[]

    return (messages as Array<Notice>)
      .concat(callLog)
      .sort((a, b) => b.timestamp - a.timestamp)
  }

  formatDatetime(datetime: string) {
    const d = dayjs(datetime)
    return dayjs(d).format(`M月D日(${dayOfWeek[d.day()]})  HH:mm`)
  }

  formatSender({ user }: Message) {
    const occupation = isRelative(user)
      ? '家族'
      : user.occupation
      ? user.occupation.name
      : ''
    return `${user.lastName}${user.firstName}${occupation}`
  }

  isOnline(worker: number) {
    return this.onlineStatus[worker] && this.onlineStatus[worker].isOnline
  }

  async loadNotice() {
    await Promise.all([
      this.$store.dispatch('message/updateUnreadHistory'),
      this.$store.dispatch('call/updateMissedHistory')
    ])
  }

  created() {
    this.$store.dispatch('team/updatePatients')
    this.loadNotice()
    const patientId = this.patientId
    if (patientId)
      this.$postMessage.addEventListener('activateApp', () => {
        this.$store.dispatch('dispensation/fetch', {
          patientId
        })
      })
    if (this.user.isPatient) this.$store.dispatch('relative/updateList')
  }

  async mounted() {
    if (!this.onLoadShowQr) return
    await this.checkIsValidLogin()
    this.$store.dispatch('videoCallRoom/setInCallWebView')
    this.showQR = true
  }

  async checkIsValidLogin() {
    try {
      await this.$api.user.fetch()
    } catch (e) {
      this.$router.replace({ name: 'PatientLogin' })
    }
  }

  launchQrReader() {
    if (this.user.isPatientRelative) {
      this.$store.dispatch('alert/showMessages', ['このボタンは無効です'])
      return
    }
    if (!isCordovaEnvironment()) {
      this.showQR = true
      return
    }
    this.$postMessage.showPatientQR()
  }

  quitQrReader() {
    if (!this.inCallWebView) {
      this.showQR = false
      return
    }
    this.$router.replace({ name: 'Hangup' })
  }
}
